import { getBackendBaseUrl } from '../utils/configUtils';
import apiInstance from './api';
import { getDataUrlFromFile, cacheLocalImage } from '../components/OfflineImageSimple';
import { invalidateSearchCache } from '../utils/searchCache';

/**
 * Service for item-related API operations (online-only version)
 */
const itemService = {
  /**
   * Get all items
   * @param {Object} params - Query parameters (limit, offset, box_id)
   * @returns {Promise} Promise object with items data
   */
  getAllItems: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      // Add parameters if provided
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.offset) queryParams.append('offset', params.offset);
      if (params.box_id) queryParams.append('box_id', params.box_id);
      // For backward compatibility
      else if (params.album_id) queryParams.append('box_id', params.album_id);
      
      // Cache-busting removed - now handled by smart cache-busting in api.js
      
      const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const response = await apiInstance.get(`/items${query}`);
      
      if (!response.data) {
        console.warn('API returned no item data');
        return [];
      }
      
      return response.data;
    } catch (error) {
      console.error('Error fetching items:', error);
      throw error;
    }
  },

  /**
   * Get item by ID
   * @param {string|number} id - Item ID
   * @returns {Promise} Promise object with item data
   */
  getItemById: async (id) => {
    try {
      // Add timestamp for cache busting
      const timestamp = new Date().getTime();
      const response = await apiInstance.get(`/items/${id}?_t=${timestamp}`);
      
      if (!response.data) {
        console.warn(`No item found with ID: ${id}`);
        return null;
      }
      
      return response.data;
    } catch (error) {
      console.error(`Error fetching item ${id}:`, error);
      throw error;
    }
  },

  /**
   * Upload item
   * @param {FormData} formData - Form data with file and metadata
   * @returns {Promise} Promise object with uploaded item data
   */
  uploadItem: async (formData) => {
    try {
      // Verify that the FormData contains a photo file
      let photoFile = null;
      // Logging for debugging
      console.log('FormData contents:');
      for (let pair of formData.entries()) {
        console.log(pair[0], pair[1]);
        if (pair[0] === 'photo' && pair[1] instanceof File) {
          photoFile = pair[1];
        }
      }
      
      if (!photoFile) {
        console.error('No photo file found in FormData');
        throw new Error('No photo file found in form data');
      }
      
      // Use the utility functions imported at the top
      
      // Get a data URL from the file for local caching
      try {
        const dataUrl = await getDataUrlFromFile(photoFile);
        console.log('Generated data URL for local caching:', dataUrl.substring(0, 50) + '...');
        
        // We don't know the item ID yet, but we'll store it temporarily and update when we get a response
        const tempId = 'temp_' + Date.now();
        cacheLocalImage(tempId, dataUrl);
        
        // Make the API call
        const response = await apiInstance.post('/items/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        if (!response.data) {
          throw new Error('API returned empty response when uploading item');
        }
        
        // Now that we have the real item ID, cache the image with that ID
        const newItemId = response.data.id;
        if (newItemId) {
          console.log(`Updating image cache for new item ${newItemId} (was temp_${tempId})`);
          cacheLocalImage(newItemId, dataUrl);
        }
        
        // Invalidate search cache since a new item was created
        invalidateSearchCache();
        
        return response.data;
      } catch (e) {
        console.error('Error processing image before upload:', e);
        
        // Continue with the upload even if local caching fails
        const response = await apiInstance.post('/items/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        if (!response.data) {
          throw new Error('API returned empty response when uploading item');
        }
        
        return response.data;
      }
    } catch (error) {
      console.error('Error uploading item:', error);
      throw error;
    }
  },

  /**
   * Update item
   * @param {string|number} id - Item ID
   * @param {Object} itemData - Item data to update
   * @returns {Promise} Promise object with updated item data
   */
  updateItem: async (id, itemData) => {
    try {
      // Add timestamp for cache busting
      const timestamp = new Date().getTime();
      const response = await apiInstance.put(`/items/${id}?_t=${timestamp}`, itemData);
      
      if (!response.data) {
        throw new Error(`Failed to update item ${id}: API returned empty response`);
      }
      
      // Invalidate search cache since item was updated
      invalidateSearchCache();
      
      return response.data;
    } catch (error) {
      console.error(`Error updating item ${id}:`, error);
      throw error;
    }
  },

  /**
   * Delete item
   * @param {string|number} id - Item ID
   * @returns {Promise} Promise object
   */
  deleteItem: async (id) => {
    try {
      console.log(`itemService: Deleting item ${id}`);
      // Add timestamp for cache busting
      const timestamp = new Date().getTime();
      const response = await apiInstance.delete(`/items/${id}?_t=${timestamp}`);
      
      console.log(`itemService: Item ${id} deleted, invalidating search cache`);
      // Invalidate search cache since item was deleted
      const newVersion = invalidateSearchCache();
      console.log(`itemService: Search cache invalidated, new version: ${newVersion}`);
      
      return response.data || { success: true, id };
    } catch (error) {
      console.error(`itemService: Error deleting item ${id}:`, error);
      throw error;
    }
  },

  /**
   * Search items
   * @param {string} query - Search query
   * @returns {Promise} Promise object with search results
   */
  searchItems: async (query) => {
    try {
      // Add timestamp for cache busting
      const timestamp = new Date().getTime();
      const response = await apiInstance.get(`/search?q=${encodeURIComponent(query)}&_t=${timestamp}`);
      
      return response.data || [];
    } catch (error) {
      console.error('Error searching items:', error);
      throw error;
    }
  },
  
  /**
   * Extract tags from an item's image
   * @param {string|number} id - Item ID
   * @returns {Promise} Promise object with extracted tags
   */
  extractTags: async (id) => {
    try {
      // Add timestamp for cache busting
      const timestamp = new Date().getTime();
      const response = await apiInstance.post(`/tags/extract/${id}?_t=${timestamp}`, {});
      
      return response.data || { tags: [] };
    } catch (error) {
      console.error(`Error extracting tags for item ${id}:`, error);
      throw error;
    }
  },
  
  /**
   * Update tags for an item
   * @param {string|number} id - Item ID
   * @param {Array} tags - Array of tags
   * @returns {Promise} Promise object with updated tags
   */
  updateTags: async (id, tags) => {
    try {
      // Add timestamp for cache busting
      const timestamp = new Date().getTime();
      const response = await apiInstance.put(`/tags/update/${id}?_t=${timestamp}`, { tags });
      
      return response.data || { success: true, tags };
    } catch (error) {
      console.error(`Error updating tags for item ${id}:`, error);
      throw error;
    }
  }
};

export default itemService;
